import { API_URLS, API_CONFIG } from '../constants';
import { logger } from '../utils/logger';

export interface IpData {
  ip: string;
  timestamp: string;
  userAgent?: string;
  country?: string;
  countryCode?: string;
  city?: string;
  region?: string;
}

export class IpService {
  private static instance: IpService;
  private cachedIpData: IpData | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  static getInstance(): IpService {
    if (!IpService.instance) {
      IpService.instance = new IpService();
    }
    return IpService.instance;
  }

  /**
   * Obtiene la IP del usuario con fallbacks múltiples
   */
  async getUserIp(token: string): Promise<IpData> {
    // Verificar cache primero
    if (this.isCacheValid()) {
      return this.cachedIpData!;
    }

    try {
      // Intento 1: Gateway Provider (servicio principal)
      const gatewayIpData = await this.getGatewayProviderIp(token);

      // Validar que la IP no sea inválida (::1, 127.0.0.1, etc.)
      if (this.isValidPublicIp(gatewayIpData.ip)) {
        this.cacheIpData(gatewayIpData);
        return gatewayIpData;
      } else {
        logger.warn('Gateway Provider devolvió IP inválida', { module: 'ip-service', ip: gatewayIpData.ip });
        throw new Error('IP inválida del gateway');
      }
    } catch (error) {
      logger.warn('Error con Gateway Provider, intentando ipdata.co', { module: 'ip-service', error });

      // Intento 2: ipdata.co (servicio secundario) - requiere API key
      if (API_CONFIG.IPDATA_API_KEY) {
        try {
          const ipData = await this.getIpFromIpdata();
          this.cacheIpData(ipData);
          return ipData;
        } catch (ipdataError) {
          logger.warn('Error con ipdata.co, intentando ipify.org', { module: 'ip-service', error: ipdataError });
        }
      } else {
        logger.debug('ipdata.co API key no configurada, saltando a ipify.org', { module: 'ip-service' });
      }

      try {
        // Intento 3: ipify.org (tercer servicio)
        const ipifyData = await this.getIpFromIpify();
        this.cacheIpData(ipifyData);
        return ipifyData;
      } catch (ipifyError) {
        logger.warn('Error con ipify.org, intentando ip-api.com', { module: 'ip-service', error: ipifyError });

        try {
          // Intento 4: ip-api.com (cuarto servicio)
          const ipApiData = await this.getIpFromIpApi();
          this.cacheIpData(ipApiData);
          return ipApiData;
        } catch (ipApiError) {
          logger.warn('Error con ip-api.com, usando IP local', { module: 'ip-service', error: ipApiError });

          // Intento 5: IP local como último recurso
          const localIpData = await this.getLocalIp();
          this.cacheIpData(localIpData);
          return localIpData;
        }
      }
    }
  }

  /**
   * Obtiene IP desde ipify.org
   */
  private async getIpFromIpify(): Promise<IpData> {
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      // Timeout configurado
      signal: AbortSignal.timeout(API_CONFIG.IP_REQUEST_TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Error ipify: ${response.status}`);
    }

    const data = await response.json();
    return {
      ip: data.ip,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
  }

  /**
   * Obtiene IP desde ipdata.co con información adicional
   */
  private async getIpFromIpdata(): Promise<IpData> {
    if (!API_CONFIG.IPDATA_API_KEY) {
      throw new Error('Missing ipdata api key');
    }

    const response = await fetch(`https://api.ipdata.co/?api-key=${encodeURIComponent(API_CONFIG.IPDATA_API_KEY)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal: AbortSignal.timeout(API_CONFIG.IP_REQUEST_TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Error ipdata: ${response.status}`);
    }

    const data = await response.json();
    return {
      ip: data.ip,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      country: data.country_name,
      countryCode: data.country_code,
      city: data.city,
      region: data.region,
    };
  }

  /**
   * Obtiene IP desde ip-api.com con información adicional
   */
  private async getIpFromIpApi(): Promise<IpData> {
    const response = await fetch('http://ip-api.com/json/', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal: AbortSignal.timeout(API_CONFIG.IP_REQUEST_TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Error ip-api: ${response.status}`);
    }

    const data = await response.json();
    return {
      ip: data.query,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      country: data.country,
      countryCode: data.countryCode,
      city: data.city,
      region: data.regionName,
    };
  }

  /**
   * Valida si una IP es una IP pública válida
   */
  private isValidPublicIp(ip: string): boolean {
    // IPs inválidas o locales que no queremos
    const invalidIps = ['::1', '127.0.0.1', 'localhost', '0.0.0.0'];

    if (invalidIps.includes(ip)) {
      return false;
    }

    // Validar formato de IPv4
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (ipv4Regex.test(ip)) {
      // Excluir rangos privados
      const privateRanges = [
        /^10\./, // 10.0.0.0/8
        /^172\.(1[6-9]|2[0-9]|3[01])\./, // 172.16.0.0/12
        /^192\.168\./, // 192.168.0.0/16
        /^127\./, // 127.0.0.0/8
        /^169\.254\./, // 169.254.0.0/16 (link-local)
        /^0\./, // 0.0.0.0/8
      ];

      return !privateRanges.some((range) => range.test(ip));
    }

    // Validar formato de IPv6 (excluir localhost ::1)
    const ipv6Regex = /^(?!::1$)(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv6Regex.test(ip);
  }

  /**
   * Obtiene IP del gatewayprovider
   */
  private async getGatewayProviderIp(token: string): Promise<IpData> {
    // Realiza una petición GET al endpoint de IP usando las constantes configuradas
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(API_URLS.IP, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(API_CONFIG.IP_REQUEST_TIMEOUT),
    });

    if (!response.ok) {
      throw new Error(`Error gatewayprovider: ${response.status}`);
    }

    const data = await response.json();
    return {
      ip: data.ip,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
  }

  /**
   * Obtiene IP local como último recurso
   */
  private async getLocalIp(): Promise<IpData> {
    // Para desarrollo local o cuando fallan los servicios externos
    return {
      ip: '127.0.0.1',
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
  }

  /**
   * Verifica si el cache es válido
   */
  private isCacheValid(): boolean {
    return this.cachedIpData !== null && Date.now() < this.cacheExpiry;
  }

  /**
   * Cachea los datos de IP
   */
  private cacheIpData(ipData: IpData): void {
    this.cachedIpData = ipData;
    this.cacheExpiry = Date.now() + this.CACHE_DURATION;
  }

  /**
   * Detecta el código de país ISO alpha-2 basado en la IP del usuario.
   * Usa directamente los providers que devuelven información geográfica
   * (ipdata.co e ip-api.com), ignorando los que solo devuelven IP.
   */
  async detectCountryCode(): Promise<string | null> {
    // Si ya hay un countryCode en cache, usarlo
    if (this.isCacheValid() && this.cachedIpData?.countryCode) {
      return this.cachedIpData.countryCode;
    }

    // Intentar ipdata.co primero (requiere API key)
    if (API_CONFIG.IPDATA_API_KEY) {
      try {
        const ipData = await this.getIpFromIpdata();
        this.cacheIpData(ipData);
        if (ipData.countryCode) return ipData.countryCode;
      } catch (error) {
        logger.warn('detectCountryCode: ipdata.co failed', { module: 'ip-service', error });
      }
    }

    // Fallback a ip-api.com
    try {
      const ipData = await this.getIpFromIpApi();
      this.cacheIpData(ipData);
      if (ipData.countryCode) return ipData.countryCode;
    } catch (error) {
      logger.warn('detectCountryCode: ip-api.com failed', { module: 'ip-service', error });
    }

    return null;
  }

  /**
   * Limpia el cache
   */
  clearCache(): void {
    this.cachedIpData = null;
    this.cacheExpiry = 0;
  }

  /**
   * Fuerza una nueva obtención de IP (ignora cache)
   */
  async refreshUserIp(token: string): Promise<IpData> {
    this.clearCache();
    return this.getUserIp(token);
  }
}

// Exportar instancia singleton
export const ipService = IpService.getInstance();
